export type CommissionType =
  | "%_gross_sales"
  | "%_gross_profit"
  | "monthly"
  | "none";

export type CommissionRoundingMode =
  | "none"
  | "nearest_dollar"
  | "up_to_dollar"
  | "down_to_dollar";

export type CommissionPaymentType = "cash" | "check" | "direct_deposit";
export type CommissionPayoutTrigger =
  | "every_collection"
  | "interval"
  | "gross_threshold"
  | "commission_threshold";
export type CommissionPayoutIntervalUnit =
  | "collection"
  | "day"
  | "week"
  | "month";
export type CommissionPaymentStatus =
  | "needs_approval"
  | "processing"
  | "needs_attention"
  | "not_due"
  | "projected"
  | "deferred"
  | "paid"
  | "reversed";

export type CommissionBankSettings = {
  paymentApproval: "manual" | "automatic";
  automaticPaymentLimitCents: number | null;
  fundingAccountId: string | null;
  payoutAccountId: string | null;
  reviewToken?: string;
};

export type CommissionSettings = {
  bankSettings?: CommissionBankSettings | null;
  type: CommissionType;
  value?: number;
  fixedAmountCents?: number;
  roundingMode?: CommissionRoundingMode;
  profitMarginPercent?: number;
  lastCommissionPaidAt?: string | null;
  payment_type?: CommissionPaymentType;
  excludeRefundsFromCommission?: boolean;
  excludeCardTransactionSurchargeFromCommission?: boolean;
  payoutTrigger?: CommissionPayoutTrigger;
  payoutIntervalCount?: number | null;
  payoutIntervalUnit?: CommissionPayoutIntervalUnit | null;
  payoutAnchorDate?: string | null;
  payoutThresholdCents?: number | null;
  revision?: number;
};

export type CommissionReportFilters = {
  paymentTypes?: string;
  startDate?: string;
  endDate?: string;
  locationId?: string;
  machineId?: string;
};

export type CommissionReportSummary = {
  earnedCents: number;
  paidCents: number;
  currentOutstandingCents: number;
  accrualCount: number;
  payoutCount: number;
};

export type CommissionPaymentMethodSummary = {
  paymentType?: CommissionPaymentType | null;
  earnedCents: number;
  paidCents: number;
  currentOutstandingCents: number;
};

export type CommissionPayoutHistoryRow = {
  id: string;
  accountId?: string | null;
  taskId: string;
  locationId?: string | null;
  locationName?: string | null;
  machineId?: string | null;
  machineName?: string | null;
  payoutCents: number;
  paymentType?: CommissionPaymentType | null;
  status: string;
  reason: string;
  paidAt: string;
  allocationCount: number;
  historical?: boolean;
};

export type CommissionPayoutHistoryPage = {
  items: CommissionPayoutHistoryRow[];
  nextCursor?: string | null;
};

export type CommissionPayoutAllocation = {
  accrualId: string;
  sourceTaskId?: string | null;
  kind: string;
  periodStart: string;
  periodEnd: string;
  earnedCents: number;
  allocatedCents: number;
  paymentType: CommissionPaymentType;
};

export type CommissionCarryForwardEntry = {
  sourceTaskId?: string | null;
  date: string;
  amountCents: number;
};

export type CommissionPayoutDetail = {
  payoutId: string;
  payoutCents: number;
  paymentType: CommissionPaymentType;
  status: string;
  reason: string;
  previousOutstandingCommissionCents: number;
  outstandingCommissionCents: number;
  paidAt: string;
  allocations: CommissionPayoutAllocation[];
};

export type AutomatedTaskType =
  | "machine_service"
  | "machine_clean"
  | "machine_refill"
  | "machine_collection";

export type AutomationSource =
  | "manual"
  | "service_cadence"
  | "revenue_threshold"
  | "cash_threshold"
  | "card_threshold"
  | "inventory_threshold"
  | "commission_threshold";

export type AutomationCancellationPolicy = "none" | "suppress_occurrence";

export type CancelledAutomationTaskPolicy = "suppress_occurrence" | "none";

export type CollectionWindowMode =
  | "scheduled_day_boundary"
  | "exact_collection_time";

export const DEFAULT_AUTOMATED_TASK_TYPES: AutomatedTaskType[] = [
  "machine_service",
  "machine_clean",
  "machine_refill",
  "machine_collection",
];

export const AUTOMATED_TASK_TYPE_LABELS: Record<AutomatedTaskType, string> = {
  machine_service: "Service Bundle",
  machine_clean: "Clean",
  machine_refill: "Refill",
  machine_collection: "Collection",
};

export type DynamicTriggerKind = "revenue" | "cash" | "card" | "commission" | "inventory";
export type DynamicSchedulingRule = { trigger: DynamicTriggerKind; machineIds: string[]; target: number; enabled: boolean };
export type DynamicRuleProgress = DynamicSchedulingRule & {
  status: "paused" | "awaiting_data" | "not_applicable" | "complete" | "insufficient_data";
  value: number | null; knownValue: number | null; matched: boolean; updatedAt: string | null;
  machines: { machineId: string; name: string; applicable: boolean; value: number | null; knownValue: number | null; complete: boolean; note: string | null; updatedAt: string | null }[];
};
export type DynamicSchedulingProgress = { rules: DynamicRuleProgress[]; paused: boolean; evaluatedAt: string };

export type Machine = {
  operationId?: string;
  id?: string;
  name?: string; // maps to backend title
  type?: string;
  makeModel?: string;
  active?: boolean;
  assigned?: boolean;
  activatedAt?: string | null;
  deactivatedAt?: string | null;
  location?: string | Location | null;
  card?: boolean;
  cash?: boolean;
  coin?: boolean;
  cardReaderManufacturer?: string;
  cardReaderDeviceId?: string;
  cardReaderMachineId?: string;
  revenueThreshold?: number | null;
  cashThreshold?: number | null;
  cardThreshold?: number | null;
  commissionThreshold?: number | null;
  disabledThresholds?: DynamicTriggerKind[];
  cardTransactionSurchargeCents?: number;
  collectionInputMode?: "dollars" | "credits";
  collectionWindowMode?: CollectionWindowMode;
  creditsPerDollar?: number;
  automatedTaskTypes?: AutomatedTaskType[];
  cancelledAutomationTaskPolicy?: CancelledAutomationTaskPolicy;
  updateOutstandingAutomationTasks?: boolean;
  commissionOverrideEnabled?: boolean;
  commission?: CommissionSettings | null;
  notes?: string;
  assets?: PhotoAsset[];
  lastServiceAt?: string | null;
  daysSinceLastService?: number | null;
  nextVisit?: string | null;
  updatedAt?: string;
  createdAt?: string;
};

export type MachineCollectionSummary = {
  machine: string;
  window?: {
    start?: string | null;
    end: string;
    basis: CollectionWindowMode;
    previousCollectionId?: string | null;
    previousCollectionDoneAt?: string | null;
    previousCollectionScheduledFor?: string | null;
  } | null;
  cashSalesCents: number;
  cardSalesCents: number;
  coinSalesCents: number;
  grossCents: number;
  transactionCount: number;
  cardTransactionCount: number;
  cashTransactionCount?: number;
  cardTransactionSurchargeTotalCents: number;
  grossAfterCardTransactionSurchargeCents: number;
  commissionCents?: number | null;
  afterCommissionCents?: number | null;
  commissionStatus?: LiveSalesCommissionStatus;
  previousGrossCents: number;
  days?: number | null;
  previousDays?: number | null;
};

export type CollectionTaskPredecessor = {
  taskId: string;
  previousTaskId: string | null;
  previousGrossCents: number;
  previousScheduledFor: string | null;
  days?: number | null;
  previousDays?: number | null;
};

export type Product = {
  id: string;
  name: string;
  brand?: string | null;
  code?: string | null;
  barcode?: string | null;
  category?: string | null;
  size?: string | null;
  unitCost?: number | null;
  defaultSellPrice?: number | null;
  unitsPerCase?: number | null;
  isActive?: boolean;
  imgUrl?: string | null;
};

export type Inventory = {
  id: string;
  title: string;
};

export type InventoryItem = {
  id: string;
  inventoryId: string;
  productId: string;
  capacity: number;
  targetStock: number;
  thresholdPercent?: number | null;
  currentStock: number;
  isActive: boolean;
};

export type MachineInventoryMode = "selection" | "pool";

export type InventoryMovementType =
  | "sale"
  | "prize_payout"
  | "refill"
  | "transfer"
  | "count";

export type InventoryMovementSource = "manual" | "task" | "dex" | "telemetry" | "expense" | "transfer";

export type MachineInventoryItem = {
  id: string;
  machineId: string;
  productId: string;
  mode: MachineInventoryMode;
  selectionCode?: string | null;
  capacity: number;
  targetStock: number;
  currentStock: number;
  sellPrice?: number | null;
  isActive: boolean;
};

export type InventoryMovement = {
  id: string;
  transferId?: string | null;
  machineInventoryItem?: string | null;
  inventoryItem?: string | null;
  movementType: InventoryMovementType;
  source: InventoryMovementSource;
  quantityDelta: number;
  refilledQuantity?: number | null;
  grossAmount?: number | null;
  stockBefore: number;
  stockAfter: number;
  notes?: string | null;
  taskId?: string | null;
  createdAt?: string | null;
};

export const MACHINE_TYPES = [
  "AI Smart Cooler",
  "Amusement",
  "Apparel",
  "ATM",
  "Auto / Car Wash Supplies",
  "Beauty / Cosmetics",
  "Bulk",
  "Capsule",
  "Change",
  "Cigarette / Vape",
  "Coffee / Hot Drinks",
  "Combo",
  "Cotton Candy",
  "Electronics / Tech Accessories",
  "EV Charger",
  "Fitness / Gym",
  "Fresh Food / Refrigerated",
  "Frozen Foods",
  "Honor Box",
  "Ice Cream",
  "Laundry / Detergent",
  "Medication / OTC",
  "Micro-Market / Self-Checkout Kiosk",
  "PPE / Personal Protection",
  "Smart Vending Machine",
  "Snack",
  "Soda",
  "Water / Ice",
  "Other",
];

export type RevenueSchedule = "same_day" | "tomorrow" | "in_2_days";
export const PAUSED_LOCATION_STATUSES = ["pending_activation", "on_hold"] as const;
export type PausedLocationStatus = typeof PAUSED_LOCATION_STATUSES[number];
export const LOCATION_STATUSES = ["active", ...PAUSED_LOCATION_STATUSES, "discontinued"] as const;
export type LocationStatus = typeof LOCATION_STATUSES[number];

export type MachineThresholdScope = "entire_location" | "matching_machines";

export type BusinessInformation = { phone?: string; website?: string };

export type Location = {
  sourceGrowthLeadId?: string;
  sourceGrowthLeadStatus?: string;
  creationWarnings?: string[];
  operationId?: string;
  id?: string;
  name?: string;
  isPublic?: boolean;
  defaultAssigneeId?: string | null;
  contactTransactionVisibility?: "hidden" | "visible";
  contactFinancialVisibility?: "commission_only" | "gross_and_commission";
  contactCommissionSettingsVisible?: boolean;
  contactRefillInventoryVisible?: boolean;
  contactTaskMetricsVisible?: boolean;
  contactTaskPhotoVisible?: boolean;
  contactLocationPhotoVisible?: boolean;
  contactTaskUpdateEmailsEnabled?: boolean;
  address?: Address;
  lastVisit?: string;
  nextVisit?: string;
  status?: LocationStatus;
  onHoldAt?: string | null;
  totalOnHoldSeconds?: number;
  daysInService?: number;
  discontinuedAt?: string;
  discontinued?: boolean;
  notes?: string;
  hours?: Record<string, { open?: string; close?: string; closed?: boolean }>;
  contacts?: LocationContact[];
  machineAssignmentInfo?: Record<
    string,
    { machine?: string; assignedAt?: string; unassignedAt?: string | null }
  >;
  machineAssignmentHistory?: Record<
    string,
    { machine?: string; assignedAt?: string; unassignedAt?: string | null }[]
  >;
  revenueSchedule?: RevenueSchedule;
  commission?: CommissionSettings;
  dynamicSchedulingRules?: DynamicSchedulingRule[];
  thresholdMachineVersions?: Record<string, string>;
  serviceCadence?: {
    frequency?: "daily" | "weekly" | "bimonthly" | "monthly" | "other";
    days?: string[];
    daysBetween?: number;
    machineThresholdScope?: MachineThresholdScope;
    requireDynamicTrigger?: boolean;
    dynamicTriggerMaxDays?: number;
    revenueThreshold?: number;
    cashThreshold?: number;
    cardThreshold?: number;
    commissionThreshold?: number;
    disabledThresholds?: DynamicTriggerKind[];
    dynamicSchedulingPaused?: boolean;
    inventoryTrigger?: number;
    custom?: string;
  };
  machines?: string[];
  assets?: PhotoAsset[];
  updatedAt?: string;
  createdAt?: string;
};

export type LocationStatusTask = {
  id: string;
  machineId: string;
  machineName: string;
  type: string;
  status: string;
  scheduledFor?: string | null;
  startedAt?: string | null;
};

export type LocationStatusImpact = {
  assignedMachines: { id: string; name: string }[];
  inProgressTasks: LocationStatusTask[];
  tasksToDelete: LocationStatusTask[];
  canPutOnHold: boolean;
};

export type ContactRole = "owner" | "manager" | "staff";

export type LocationContact = {
  id?: string;
  userId?: string;
  active?: boolean;
  inviteStatus?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  role?: ContactRole;
};

export type ServiceCadenceTaskReschedulePreview = {
  taskId: string;
  machineId: string;
  machineName: string;
  currentScheduledFor: string;
  newScheduledFor: string;
};

export type PendingInvite = {
  inviteId: string;
  targetType: "organization_user" | "location_contact";
  organizationId?: string;
  organizationName?: string;
  locationId?: string;
  locationName?: string;
  role?: ContactRole | string;
};

export type TaskStatus =
  | "pending"
  | "done"
  | "unassigned"
  | "error"
  | "cancelled";
export type TaskCleanupStatus =
  | "current"
  | "warning_due"
  | "warning_sent"
  | "cancellation_due";
export type TaskType =
  | "default"
  | "machine_remove"
  | "machine_install"
  | "machine_clean"
  | "machine_repair"
  | "machine_refill"
  | "machine_refund"
  | "machine_collection"
  | "machine_service"
  | "machine_pickup_inventory"
  | "location_lead"
  | "other";

export type TaskAssetType = "confirmation_photo";

export type PhotoAsset = {
  id: string;
  type: "photo";
  url?: string | null;
  createdAt?: string;
  uploadedBy?: string | null;
  machineId?: string;
  locationId?: string;
};

export type TaskAsset = {
  id: string;
  taskId: string;
  type: TaskAssetType;
  url?: string | null;
  createdAt: string;
  uploadedBy?: string | null;
};

export type TaskInventoryCompletionLine = {
  itemId: string;
  product: Product;
  stockBefore: number;
  refilledQuantity: number;
  finalStock: number;
  movementId?: string | null;
};

export type TaskInventoryCompletion = {
  items: TaskInventoryCompletionLine[];
};

export type TaskPickupLine = {
  id: string;
  refillTaskId?: string | null;
  product: Product;
  machineName?: string | null;
  currentStock?: number | null;
  capacity?: number | null;
  suggestedQuantity: number;
  pickedUpQuantity?: number | null;
};

export type Task = {
  id: string;
  correctionOfId?: string | null;
  correctedById?: string | null;
  executionTaskId?: string;
  title?: string;
  type: TaskType;
  status: TaskStatus;
  isPublic?: boolean;
  creatorId?: string | null;
  organizationId?: string | null;
  userId?: string | null;
  assigneeName?: string | null;
  assigneeEmail?: string | null;
  assigneePhotoUrl?: string | null;
  machineId?: string | null;
  machineName?: string | null;
  serviceTaskId?: string | null;
  collectionInputMode?: "dollars" | "credits";
  creditsPerDollar?: number;
  locationId?: string | null;
  locationName?: string | null;
  locationAddress?: Address | null;
  growthLeadId?: string | null;
  workMode?: "remote" | "field";
  action?: string;
  customAction?: string;
  address?: Address | null;
  latitude?: number | null;
  longitude?: number | null;
  routable?: boolean;
  notes?: string;
  createdAt: string;
  scheduledFor?: string | null;
  startedAt?: string;
  doneAt?: string;
  cleanupStatus?: TaskCleanupStatus;
  cleanupWarningSentAt?: string | null;
  cleanupCancellationDate?: string | null;
  isLive?: boolean;
  distance?: number | null;
  duration?: number | null;
  grossCents?: number | null;
  grossCashCents?: number | null;
  grossCardCents?: number | null;
  refundsCents?: number | null;
  commissionCents?: number | null;
  commissionEarnedCents?: number | null;
  commissionPayoutCents?: number | null;
  previousOutstandingCommissionCents?: number | null;
  outstandingCommissionCents?: number | null;
  commissionPaymentStatus?: CommissionPaymentStatus;
  commissionPaymentReason?: string | null;
  commissionPaymentDeferred?: boolean;
  commissionPaymentType?: CommissionPaymentType | null;
  commissionCarryForward?: CommissionCarryForwardEntry[];
  netCents?: number | null;
  cardTransactionCount?: number | null;
  cardTransactionSurchargeTotalCents?: number | null;
  financialsOverridden?: boolean | null;
  prefillUpdatedAt?: string | null;
  collectionWindowStart?: string | null;
  collectionWindowEnd?: string | null;
  collectionWindowBasis?: CollectionWindowMode;
  lastVisitAt?: string | null;
  daysSinceLastVisit?: number | null;
  inventoryCompletion?: TaskInventoryCompletion | null;
  inventorySourceMode?: "warehouse" | "van" | "untracked";
  inventorySourceWarehouseId?: string | null;
  inventorySourceWarehouseName?: string | null;
  inventorySourceVanId?: string | null;
  inventorySourceVanName?: string | null;
  destinationVanId?: string | null;
  destinationVanName?: string | null;
  warehouseId?: string | null;
  warehouseName?: string | null;
  warehouseAddress?: Address | null;
  refillTaskId?: string | null;
  refillTaskIds?: string[];
  pickupLines?: TaskPickupLine[];
  assets?: TaskAsset[];
  automationSource?: AutomationSource;
  automationReasons?: { label: string; description: string; machine_id?: string | null; threshold?: number }[];
  automationCancellationPolicy?: AutomationCancellationPolicy;
  manualScheduleOverrideAt?: string | null;
  manualScheduleOverrideBy?: string | null;
  manualScheduleOverrideByName?: string | null;
  manualScheduleOverrideByEmail?: string | null;
  manualScheduleOverrideFrom?: string | null;
  manualScheduleOverrideSource?: AutomationSource | null;
};

export type TaskPrecheckReason =
  | "same_day_existing"
  | "no_payment_method"
  | "machine is inactive";

export type TaskPrecheckExistingTask = {
  id: string;
  type: TaskType;
  scheduledFor: string;
  locationId?: string | null;
  machineId: string;
};

export type TaskPrecheckResponse = {
  ok: boolean;
  reason?: TaskPrecheckReason | null;
  existingTask?: TaskPrecheckExistingTask | null;
};

export type OperatorUser = {
  photoUrl?: string | null;
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  isOperator?: boolean;
};

export type InventoryVan = {
  id: string;
  name: string;
  inventoryId: string;
  isActive: boolean;
  assignedUserIds: string[];
};

export type OrganizationMember = {
  photoUrl?: string | null;
  canApproveCommissionPayments?: boolean;
  canLinkExpenseAssociations?: boolean;
  id?: string | null;
  user_id?: string | null;
  userId?: string | null;
  invitee_email: string;
  inviteeEmail?: string;
  first_name?: string | null;
  firstName?: string | null;
  last_name?: string | null;
  lastName?: string | null;
  phone?: string | null;
  role: OrganizationMemberRole;
  status: OrganizationMemberStatus;
  expires_at?: string | null;
  expiresAt?: string | null;
};

export type OrganizationMemberRole = "owner" | "admin" | "operator";
export type OrganizationMemberStatus =
  | "none"
  | "pending"
  | "accepted"
  | "revoked"
  | "expired";

export type RoutePlannerMachineMetric = {
  machineId: string;
  machineName: string;
  grossCents: number;
  grossCashCents?: number;
  grossCardCents?: number;
  cardTransactionCount?: number;
  cardTransactionSurchargeTotalCents?: number;
  grossAfterCardTransactionSurchargeCents?: number;
  lastServiceAt?: string | null;
  daysSinceLastService?: number | null;
};

export type RoutePlannerLocationMetric = {
  locationId: string;
  locationName: string;
  grossCents: number;
  commissionCents: number;
  netCents: number;
  machines: RoutePlannerMachineMetric[];
};

export type ConfigTab = "integrations";

export type Address = {
  id?: string;
  name?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  latitude?: number | null;
  longitude?: number | null;
  timezone?: string;
};

export type Warehouse = {
  id?: string;
  name?: string;
  inventoryId?: string;
  address?: Address;
  organization?: string;
  isPublic?: boolean;
  isActive?: boolean;
};

export type IntegrationConfig = {
  telemetryProviders?: TelemetryProviderEntry[];
  webhooksEnabled?: boolean;
  webhookUrl?: string;
  notificationsEmail?: string;
};
export type MapStop = {
  ownerId?: string;
  vehicleAccentColor?: string;
  loadDescription?: (signal: AbortSignal) => Promise<string>;
  description?: string;
  labelTimestamp?: string | null;
  statusLabel?: string;
  statusTone?: import("./components/StatusPill").StatusPillTone;
  lat: number;
  lng: number;
  label?: string;
  id?: string;
  color?: string;
  markerStyle?: "pin" | "dot" | "vehicle";
  photoUrl?: string | null;
  animatePosition?: boolean;
  positionCapturedAt?: string;
  speedMps?: number | null;
  horizontalAccuracyMeters?: number | null;
  headingDegrees?: number | null;
  actionLabel?: string;
  actionIconHtml?: string;
  actionActive?: boolean;
  actionDisabled?: boolean;
  popupPinned?: boolean;
  onAction?: () => void;
  onPopupClose?: () => void;
};
export type RouteGeometry = {
  type: "LineString";
  coordinates: [number, number][];
};
export type MapRoute = RouteGeometry & {
  ownerId?: string;
  opacity?: number;
  dashed?: boolean;
  id: string;
  trailStopId?: string;
  color?: string;
  label?: string;
  caption?: string;
  captionMetrics?: { time: string; distance: string };
  captionGroup?: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
};
export type TelemetryProviderEntry = {
  id?: string;
  provider?: string;
  apiKey?: string;
  hasSecret?: boolean;
  cardTransactionSurchargeCents?: number;
  applyCardTransactionSurchargeToMachines?: boolean;
  methods?: TelemetryMethod[];
  methodConfigUpdates?: TelemetryMethodConfigUpdate[];
};

export type TelemetryMethodType =
  | "polling"
  | "direct_push"
  | "sqs_push"
  | "file_import";

export type TelemetryMethodStatus =
  | "not_configured"
  | "configured"
  | "pending"
  | "active"
  | "stale"
  | "error";

export type TelemetryMethod = {
  id: string;
  methodType: TelemetryMethodType;
  status: TelemetryMethodStatus;
  enabled: boolean;
  priority: number;
  frequency?: number | null;
  publicConfig: Record<string, unknown>;
  maskedPrivateConfig: Record<string, unknown>;
  hasPrivateConfig: boolean;
  lastSyncAt?: string | null;
  lastReceivedAt?: string | null;
  lastError?: string | null;
};

export type TelemetryMethodSyncUpdate = {
  id: string;
  telemetryId: string;
  provider: string;
  methodType: TelemetryMethodType;
  status: TelemetryMethodStatus;
  enabled: boolean;
  priority: number;
  lastSyncAt?: string | null;
  lastReceivedAt?: string | null;
};

export type TelemetryMethodConfigPayload = {
  enabled?: boolean | null;
  status?: TelemetryMethodStatus | null;
  priority?: number | null;
  frequency?: number | null;
  publicConfig?: Record<string, unknown> | null;
  privateConfig?: Record<string, unknown> | null;
};

export type TelemetryMethodConfigUpdate = TelemetryMethodConfigPayload & {
  id: string;
};

export type Integration = {
  id?: string;
  name?: string;
  service_type?: string;
  api_key?: string;
  has_secret?: boolean;
  card_transaction_surcharge_cents?: number;
  monitor?: boolean;
  monitor_frequency?: number;
  methods?: TelemetryMethod[];
  updated_at?: string;
};

export type AutomationStatus = "started" | "stopped";
export type TelemetryMonitorStatusResponse = {
  status: AutomationStatus;
};

export type MachineSale = {
  id?: string;
  transactionId?: string;
  machine?: string;
  location?: string | null;
  timezone?: string | null;
  localDate?: string | null;
  paymentMethod?: string;
  cardNumber?: string;
  cashSalesCents?: number;
  cardSalesCents?: number;
  coinSalesCents?: number;
  commissionCents?: number | null;
  afterCommissionCents?: number | null;
  commissionStatus?: LiveSalesCommissionStatus;
  commissionSource?: "machine" | "location" | "none";
  expenseCardSurchargeCents?: number;
  expenseCardFeeBaseCents?: number;
  machineNameSnapshot?: string;
  locationNameSnapshot?: string;
  isVoided?: boolean;
  voidReason?: string | null;
  voidedAt?: string | null;
  timestamp?: string;
};

export type SalesComparisonPeriod = {
  start: string;
  end: string;
  timezone: string;
};

export type LiveSalesCommissionStatus =
  | "complete"
  | "partial"
  | "not_applicable"
  | "pending";

export type MachineTelemetrySnapshot = {
  todaySales?: number;
  sinceLastService?: number;
  lastSync?: string | null;
};

export const photoAssets = (assets?: PhotoAsset[] | null): PhotoAsset[] =>
  (assets || []).filter((asset) => asset.type === "photo" && Boolean(asset.url));

export type DefaultAssigneeApplyScope = "pending_and_unassigned" | "unassigned_only" | "none";
